<template lang="pug">
  .container(id="Playlog-container")
    h4 {{`${entity.type} Playlog - ${getEntityName}`}}
    download-excel(:data = "formatIntervalsForExcel", :title='getExcelHeader')
      el-button.float-right(id="Playlog-downloadExcelButton" type='text', size="mini", :title='$t("Download excel file")')
        font-awesome-icon.medium-icon(icon="download")
    span.float-right &nbsp;&nbsp;&nbsp;
    el-button.float-right(id="Playlog-showNetworkLogButton" v-if='entity.type !== "Network"', type='text', size="mini", @click='showPlaylogForNetwork()', :title='$t("Show whole network log")')
      font-awesome-icon.medium-icon(icon="bars")
    el-form.search-form-inline(:inline='true', size="mini")
      el-form-item.label(:label='$t("Show:")')
        el-select(id="Playlog-showSelect" v-model='period', :placeholder='$t("Interval")')
          el-option.label(:label='$t("hours")', value='hour')
          el-option.label(:label='$t("days")', value='day')
          el-option.label(:label='$t("weeks")', value='week')
          el-option.label(:label='$t("months")', value='month')
          el-option.label(:label='$t("quarters")', value='quarter')
          el-option.label(:label='$t("years")', value='year')
      el-form-item.label(v-if='entity.type !== "Medium"', :label='$t("media")')
        el-checkbox(id="Playlog-displayMediaCheckbox" v-model='displayMedia')
      el-form-item.label(v-if='entity.type !== "Device"', :label='$t("devices")')
        el-checkbox(id="Playlog-displayDevicesCheckbox" v-model='displayDevices')
      el-form-item.label(:label='$t("")')
        span &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      el-form-item.label(:label='$t("Start date")')
        el-date-picker(:picker-options="{ firstDayOfWeek: 1 }", id="Playlog-startDatePicker" v-model='startDate', type="date", :placeholder='$t("Start")')
      el-form-item.label(:label='$t("End date")')
        el-date-picker(:picker-options="{ firstDayOfWeek: 1 }", id="Playlog-endDatePicker" v-model='endDate', type="date", :placeholder='$t("End")')
      el-form-item
        el-button(id="Playlog-generateButton" :disabled='loading', type='success', @click='loadData') {{ loading ? $t('...generating') : $t('Generate') }}
    .section(id="Playlog-noDataSection" v-if='intervals.length === 0')
      h4
        p {{ $t('No data collected')}}
    .section.selectable(id="Playlog-dataSection" v-if='intervals.length > 0')
      .interval.table-header
        .cell.period {{ $t('Period') }}
        .cell.medium(v-if='entity.type !== "Medium" && intervals[0].Medium') {{ $t('Medium') }}
        .cell.device(v-if='entity.type !== "Device" && intervals[0].Device') {{ $t('Device') }}
        .cell.played {{ $t('Played items') }}
        .cell.duration {{ $t('Duration') }}
        .cell.interrupts {{ $t('Interrupts') }}
      .interval.body(v-for="(interval, index) of intervals", :class='{ "day-transition": intervals[index -1] && (intervals[index -1].periodStart !== interval.periodStart) }')
        .cell.period
          span(
            v-if='intervals[index -1] && (intervals[index -1].periodStart !== interval.periodStart) || index === 0'
            )
            span {{ $moment(interval.periodStart).format(period === "hour" ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD') }}
            span(v-if='period === "hour"') &nbsp;-&nbsp;
              span {{ $moment(interval.periodStart).add(1, 'h').format('HH:mm') }}
        .cell.medium(v-if='entity.type !== "Medium" && intervals[0].Medium', @click='showPlaylogForMedium(interval.Medium)') {{ getMedium(interval.Medium) ? getMedium(interval.Medium).name : `${interval.MediumName}${interval.Medium === 0 ? $t(' - deleted') : ''}` }}
        .cell.device(v-if='entity.type !== "Device" && intervals[0].Device', @click='showPlaylogForDevice(interval.Device)') {{ getDevice(interval.Device).name }}
        .cell.played {{ interval.Finishes }}
        .cell.duration {{ msToDuration(interval.Duration) }}
        .cell.interrupts {{ interval.Interrupts }}
      .interval.footer
        .cell.period {{ $t('Period') }}
        .cell.medium(v-if='entity.type !== "Medium" && intervals[0].Medium') {{ $t('Medium') }}
        .cell.device(v-if='entity.type !== "Device", intervals[0].Device') {{ $t('Device') }}
        .cell.played {{ getSumOf('Finishes') }}
        .cell.duration {{ msToDuration(getSumOf('Duration')) }}
        .cell.interrupts {{ getSumOf('Interrupts') }}
</template>
<script>
import Vue from 'vue'
import downloadExcel from 'vue-json-excel'
import textUtils from './textUtils.mixin'

export default {
  mixins: [textUtils],
  components: {
    downloadExcel
  },
  async created () {
    await this.loadData()
  },
  props: {
    'entity': {
      required: false
    }
  },
  watch: {
    entity: async function (newVal, oldVal) {
      if (newVal.type !== oldVal.type) {
        this.intervals = []
        this.displayDevices = false
        this.displayMedia = false
        await this.loadData()
      }
    }
  },
  data () {
    return {
      errors: [],
      period: 'day',
      from: this.$moment().subtract(1, 'w').valueOf(),
      to: this.$moment().valueOf(),
      intervals: [],
      startDate: this.$moment().subtract(1, 'w').format('YYYY-MM-DD'),
      endDate: this.$moment().format('YYYY-MM-DD'),
      displayDevices: false,
      displayMedia: false,
      loading: true
    }
  },
  methods: {
    showPlaylogForMedium (mediumId) {
      if (!mediumId) return console.log('showPlaylogForMedium', mediumId)
      this.$emit('showPlaylogForMedium', mediumId)
    },
    showPlaylogForDevice (deviceId) {
      this.$emit('showPlaylogForDevice', deviceId)
    },
    showPlaylogForNetwork () {
      this.$emit('showPlaylogForNetwork')
    },
    async loadData () {
      this.loading = true
      const groupBy = {
        'Device': this.displayDevices,
        'Medium': this.displayMedia
      }
      await Vue.axios
        .post(`playlog/generate`,
          {
            entity: this.entity,
            groupBy,
            networkId: this.entity.id,
            from: this.$moment(this.startDate).valueOf(),
            to: this.$moment(this.endDate).add(24, 'h').valueOf(),
            interval: this.period
          })
        .then(response => {
          this.intervals = response.data
        })
      this.loading = false
    },
    getSumOf (property) {
      if (this.intervals.length === 0) return 0
      return this.intervals.map(int => parseInt(int[property])).reduce((prev, next) => prev + next)
    },
    getMedium (mediumId) {
      return this.$store.state.Medium.media_list.find(med => med.id === parseInt(mediumId))
    },
    getDevice (deviceId) {
      return this.$store.state.Device.device_list.find(dev => dev.id === parseInt(deviceId))
    },
    getNetwork (networkId) {
      return this.$store.state.User.user.Networks.find(network => network.id === this.$store.state.User.user.lastNetworkId)
    }
  },
  computed: {
    getExcelHeader () {
      return `${this.entity.type} Playlog for ${this.getEntityName} | ${this.$moment(this.startDate).format('YYYY-MM-DD')} - ${this.$moment(this.endDate).format('YYYY-MM-DD')} | ${this.period}`
    },
    getEntityName () {
      if (!this.intervals[0]) return '...'
      if (!this[`get${this.entity.type}`](this.entity.id)) {
        console.log(this.intervals[0].MediumName)
        return `Deleted medium ${this.intervals[0].MediumName}`
      }
      return this[`get${this.entity.type}`](this.entity.id).name
    },
    formatIntervalsForExcel () {
      const intervalsPretty = []
      for (const interval of this.intervals) {
        const intervalPretty = {}
        intervalPretty['Period start'] = this.$moment(interval.periodStart).format('YYYY-MM-DD HH:mm:ss')
        intervalPretty['Period end'] = this.$moment(interval.periodStart).add(1, this.period[0]).format('YYYY-MM-DD HH:mm:ss')
        for (const entity of ['Medium', 'Device']) {
          if (interval[`${entity}Name`]) intervalPretty[entity] = interval[`${entity}Name`]
        }
        intervalPretty.Finishes = interval.Finishes
        intervalPretty['Duration milliseconds'] = interval.Duration
        intervalPretty.Duration = this.msToDuration(interval.Duration)
        intervalPretty.Interrupts = interval.Interrupts
        intervalsPretty.push(intervalPretty)
      }
      return intervalsPretty
    }
  }
}
</script>
<style lang="scss" scoped>
@import "../../../theme-m4b/src/common/var.scss";
.interval {
  &.hidden {
    display: none;
  }
  &.warning {
    background-color: rgba($--color-danger, 0.2);
  }
  &.table-header {
    margin-bottom: 10px;
    padding: 5px 0px 5px 0px;
    border-top: $--border-light;
    border-bottom: $--border-light;
  }
  &.body {
    padding: 10px 0px;
    color: $--color-text-secondary;
    border-bottom: $--border-extra-light;
  }
  &.day-transition {
    border-top: $--border-light;
  }
  display: flex;
  font-size: $--font-size-base;
  .cell {
    width: 25%;
    &.medium, &.device {
      cursor: pointer;
    }
  }
  .cell-wider {
    width: 33%;
    border: 0px dotted pink;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Common/Playlog.vue